/** Due Diligence API service. */

import { apiClient } from '../client';
import { API_ENDPOINTS } from '../endpoints';
import { withDedupe } from '@/utils/requestDedupe';

/** Query param values for `GET /api/v1/due_diligence?window=` (matches backend DueDiligenceWindow). */
export const DUE_DILIGENCE_WINDOWS = ['24h', '7d', '30d', '3m', '6m', '1y'] as const;
export type DueDiligenceWindow = (typeof DUE_DILIGENCE_WINDOWS)[number];

export function isDueDiligenceWindow(value: string): value is DueDiligenceWindow {
  return (DUE_DILIGENCE_WINDOWS as readonly string[]).includes(value);
}

export interface DueDiligencePost {
  ticker: string; // Primary ticker (for backward compatibility)
  all_tickers: string | null; // Comma-separated list of all tickers
  timestamp: string;
  post_title: string | null;
  post_url: string;
  author_name: string | null;
  author_karma: number | null;
  sentiment: number | null;
  subreddit: string | null;
}

export interface DueDiligenceResponse {
  posts: DueDiligencePost[];
  pagination?: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  next_cursor?: string;
}

export async function fetchDueDiligencePosts(
  ticker?: string,
  options?: { window?: DueDiligenceWindow }
): Promise<DueDiligencePost[]> {
  const window: DueDiligenceWindow = options?.window ?? (ticker ? '30d' : '7d');
  const scope = ticker ? encodeURIComponent(ticker) : 'all';
  return withDedupe(`dd:v3:${scope}:${window}`, async () => {
    try {
      const params = new URLSearchParams();
      if (ticker) {
        params.set('ticker', ticker);
      }
      params.set('window', window);
      params.set('page', '1');
      params.set('page_size', '100');
      const endpoint = `${API_ENDPOINTS.dueDiligence}?${params.toString()}`;

      const response = await apiClient.get<DueDiligenceResponse>(endpoint);
      return response.data?.posts || [];
    } catch (error) {
      console.error('Error fetching due diligence posts:', error);
      throw error;
    }
  });
}

export async function fetchDueDiligencePage(options: {
  page: number;
  pageSize: number;
  window: DueDiligenceWindow;
  timeOrder: 'asc' | 'desc';
}): Promise<DueDiligenceResponse> {
  const params = new URLSearchParams();
  params.set('page', String(options.page));
  params.set('page_size', String(options.pageSize));
  params.set('window', options.window);
  params.set('time_order', options.timeOrder);
  const url = `${API_ENDPOINTS.dueDiligence}?${params.toString()}`;
  return withDedupe(`dd:list:v1:${url}`, async () => {
    const response = await apiClient.get<DueDiligenceResponse>(url);
    return response.data;
  });
}
